import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { InventoryBalance } from '@app/svf/models/inventory-balance.model';
import { InventoryApiService } from '@app/svf/services/inventory-api.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { AvatarModule } from 'primeng/avatar';
import { FloatLabel } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TableModule, TablePageEvent } from 'primeng/table';
import { catchError, finalize, of } from 'rxjs';

@Component({
  selector: 'inventory-balance',
  imports: [
    AvatarModule,
    DecimalPipe,
    FloatLabel,
    FormsModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    MessageModule,
    SelectModule,
    TableModule,
  ],
  templateUrl: './inventory-balance-modal.component.html',
  styleUrl: './inventory-balance-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InventoryBalanceModalComponent {
  private readonly inventoryApiService = inject(InventoryApiService);
  private readonly modalService = inject(BsModalService);

  readonly modalId = input<string>('');

  protected readonly errorMessage = signal('');
  protected readonly isLoading = signal(true);
  protected readonly searchTerm = signal('');
  protected readonly selectedMaterial = signal<string | null>(null);
  protected readonly selectedProductType = signal<string | null>(null);
  protected readonly selectedProfile = signal<string | null>(null);
  protected readonly selectedFunction = signal<string | null>(null);
  protected readonly first = signal(0);
  protected readonly rows = signal(10);
  protected readonly rowsPerPageOptions = [5, 10, 25, 50];

  protected readonly inventoryBalances = toSignal(
    this.inventoryApiService.getInventoryBalances().pipe(
      catchError(() => {
        this.errorMessage.set('Kunde inte hämta lagersaldo från servern');
        return of([] as InventoryBalance[]);
      }),
      finalize(() => this.isLoading.set(false))
    ),
    { initialValue: [] as InventoryBalance[] }
  );

  protected readonly normalizedSearchTerm = computed(() =>
    this.normalize(this.searchTerm())
  );

  protected readonly materialOptions = computed(() =>
    this.uniqueOptions(this.inventoryBalances().map((item) => item.material))
  );

  protected readonly productTypeOptions = computed(() =>
    this.uniqueOptions(this.inventoryBalances().map((item) => item.type))
  );

  protected readonly profileOptions = computed(() =>
    this.uniqueOptions(this.inventoryBalances().map((item) => item.profile))
  );

  protected readonly functionOptions = computed(() =>
    this.uniqueOptions(this.inventoryBalances().map((item) => item.function))
  );

  protected readonly hasActiveFilters = computed(() =>
    Boolean(
      this.normalizedSearchTerm() ||
      this.selectedMaterial() ||
      this.selectedProductType() ||
      this.selectedProfile() ||
      this.selectedFunction()
    )
  );

  protected readonly veryHighBalance = computed(() =>
    this.inventoryBalances().filter((item) => item.balance > 15).length
  );

  protected readonly highBalance = computed(() =>
    this.inventoryBalances().filter(
      (item) => item.balance >= 11 && item.balance < 16
    ).length
  );

  protected readonly mediumBalance = computed(() =>
    this.inventoryBalances().filter(
      (item) => item.balance >= 6 && item.balance < 11
    ).length
  );

  protected readonly lowBalance = computed(() =>
    this.inventoryBalances().filter(
      (item) => item.balance >= 0 && item.balance < 6
    ).length
  );

  protected readonly filteredInventoryBalances = computed(() => {
    const searchTerm = this.normalizedSearchTerm();
    const material = this.selectedMaterial();
    const productType = this.selectedProductType();
    const profile = this.selectedProfile();
    const productFunction = this.selectedFunction();

    return this.inventoryBalances().filter((item) =>
      (!searchTerm || this.matchesSearch(item, searchTerm)) &&
      (!material || item.material === material) &&
      (!productType || item.type === productType) &&
      (!profile || item.profile === profile) &&
      (!productFunction || item.function === productFunction)
    );
  });

  protected updateSearch(value: string): void {
    this.searchTerm.set(value);
    this.resetPage();
  }

  protected updateMaterial(value: string | null): void {
    this.selectedMaterial.set(value);
    this.resetPage();
  }

  protected updateProductType(value: string | null): void {
    this.selectedProductType.set(value);
    this.resetPage();
  }

  protected updateProfile(value: string | null): void {
    this.selectedProfile.set(value);
    this.resetPage();
  }

  protected updateFunction(value: string | null): void {
    this.selectedFunction.set(value);
    this.resetPage();
  }

  protected clearFilters(): void {
    this.searchTerm.set('');
    this.selectedMaterial.set(null);
    this.selectedProductType.set(null);
    this.selectedProfile.set(null);
    this.selectedFunction.set(null);
    this.resetPage();
  }

  protected onPage(event: TablePageEvent): void {
    this.first.set(event.first);
    this.rows.set(event.rows);
  }

  protected close(): void {
    this.modalService.hide(this.modalId());
  }

  private resetPage(): void {
    this.first.set(0);
  }

  private matchesSearch(item: InventoryBalance, searchTerm: string): boolean {
    return this.normalize(item.itemId).includes(searchTerm) ||
      this.normalize(item.itemName).includes(searchTerm);
  }

  private normalize(value: string): string {
    return value.trim().toLocaleLowerCase('sv-SE');
  }

  private uniqueOptions(values: readonly string[]): string[] {
    return [...new Set(values.filter((value) => value.trim()))]
      .sort((firstValue, secondValue) =>
        firstValue.localeCompare(secondValue, 'sv-SE', { sensitivity: 'base' })
      );
  }
}
